import { defineComponent as _defineComponent } from 'vue'
import { reactive, computed, onMounted, ref } from "vue";
  import { ListingStatus } from "@/enums/ListingStatus";
  import { StatesEnum } from "@/enums/StatesEnum";
  import { required, minValue } from "@vuelidate/validators";
  import useVuelidate from "@vuelidate/core";
  import DatePicker from "@/components/ui/DatePicker.vue";
  import store from "@/store";
  import Cleave from "vue-cleave-component";
  import { phoneNumberOption } from "@/helpers/cleavePlugin";
  import { POSITION, useToast } from "vue-toastification";
  import { UserInterface } from "@/types/UserInterface";
  import router from "@/router";
  import ImageFilesInterface from "@/types/ImagesFilesInterface";
  import dayjs from "dayjs";
  import { ListingEditStatus } from "@/enums/ListingEditStatus";
  import DropDown from "@/components/ui/dropdown/DropDown.vue";
  import getUserData from "@/helpers/listing/getUserData";
  import { ImagesInterface } from "@/types/ListingInterface";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'EditListing',
  setup(__props, { expose: __expose }) {
  __expose();

  const loading = ref(true);
  const agentList = ref<UserInterface[]>([]);
  const listing = ref<any>({});
  const toast = useToast();
  const listingId = computed(() => router.currentRoute.value.params.listingId);
  const maxDate = dayjs().format("YYYY-MM-DD");
  const isUploadingImage = ref(false);

  onMounted(() => {
    getListing();
    getUsersSalesPerson();
  });

  const formFields = {
    headline: null,
    address: {
      state: null,
      street: null,
      streetNumber: null,
      suburb: null,
      postcode: null,
      lotNumber: null,
    },
    area: null,
    price: null,
    description: null,
    imageIdCounter: 0,
    imageFiles: [] as ImageFilesInterface[],
    images: [] as ImagesInterface[],
    selectedImage: null as unknown as ImagesInterface,
    agent: getUserData().landownerId,
    agentName: getUserData().name,
    agentEmail: getUserData().email,
    status: ListingStatus.PUBLISHED,
    phone: null,
    soldDetails: {
      soldDate: null,
      soldPrice: null,
    },
  };

  const form = reactive({ ...formFields });

  const rules = {
    address: {
      state: { required },
      street: { required },
      streetNumber: { required },
      suburb: { required },
      postcode: { required },
      lotNumber: { required },
    },
    area: { required },
    price: { required },
    description: { required },
    selectedImage: { required },
    agent: { required },
    phone: { required },
    headline: { required },
    status: { required },
  };

  const v$ = useVuelidate(rules, form);

  const getUsersSalesPerson = () => {
    loading.value = true;
    store
      .dispatch("user/getUsersSalesPerson", {})
      .then((res) => {
        res.data.push({ ...store.state.user.data });
        agentList.value = res.data;
      })
      .finally(() => {
        loading.value = false;
      });
  };

  const getListing = async () => {
    try {
      const res = await store.dispatch("listing/getListing", listingId.value);

      listing.value = res.data;
      form.agent = res.data.salesPersonListingId;
      form.images = res.data.images;
      form.selectedImage = form.images[0];
      Object.assign(form, {
        ...listing.value,
        status: ListingStatus.PUBLISHED,
      });
    } catch (error) {
      toast.error("An error occurred while getting the listings");
    } finally {
      loading.value = false;
    }
  };

  const uploadImage = (e: any) => {
    if (e.target?.files && e.target.files[0]) {
      const reader = new FileReader();

      reader.onload = async (event) => {
        isUploadingImage.value = true;
        const uploadedImage = await uploadImageRequest({
          id: form.imageIdCounter,
          data: event.target?.result,
          buffer: e.target.files[0] ?? null,
        });

        form.images.push(...uploadedImage);
        isUploadingImage.value = false;
      };
      reader.readAsDataURL(e.target.files[0]);
      form.imageIdCounter += 1;
    }
  };

  const changeAgent = (agent: UserInterface) => {
    form.agent = agent._id;
    form.agentName = `${agent.firstName} ${agent.lastName}`;
    form.agentEmail = agent.email;
  };
  const orderImages = () => {
    const mainImage = form.images.find(
      (image) => image.url === form.selectedImage.url
    ) as ImagesInterface;

    return [
      mainImage,
      ...form.images.filter((image) => image.url !== mainImage.url),
    ];
  };
  const saveListing = async (e: Event) => {
    e.preventDefault();
    v$.value.$touch();
    const isFormCorrect = await v$.value.$validate();

    let agentId = listing.value.agentId;
    const userData = listing.value;
    const requestData = {
      ...form,
      landownerId: userData.landownerId,
      estateId: userData.estateId,
      name: form.agentName,
      email: form.agentEmail,
      agentId,
      images: orderImages(),
      salesPersonListingId: form.agent,
    };

    if (!isFormCorrect) {
      return toast.error("The form is not completed or some data is invalid");
    }

    if (!agentId) {
      return toast.error("This estate does not have an agentId configured");
    }

    try {
      if (form.status === ListingStatus.PUBLISHED) {
        delete requestData.soldDetails;
        const res = await store.dispatch("listing/updateListing", {
          id: listingId.value,
          data: requestData,
        });

        if (res.status >= 200 && res.status < 300) {
          toast.success("Listing Updated");
          setTimeout(() => {
            router.push("/listing");
          }, 2500);
          return res;
        }

        return res;
      }

      if (form.status === ListingStatus.SOLD) {
        const res = await store.dispatch("listing/soldListing", {
          id: listingId.value,
          data: requestData,
        });

        if (res.status >= 200 && res.status < 300) {
          toast.success("Listing status is being changed to Sold");
          setTimeout(() => {
            router.push("/listing");
          }, 2500);
          return res;
        }

        return res;
      }

      return;
    } catch (err) {
      toast.error("Something went wrong, try later");
      return;
    }
  };

  const cancelListing = () => {
    router.push("/listing");
  };

  const uploadImageRequest = async (image: any) => {
    const formData = new FormData();
    const mainImage = image.id === form.selectedImage ? image : null;

    if (mainImage) {
      formData.append("files", mainImage?.buffer);
    }

    if (image.id !== form.selectedImage) {
      formData.append("files", image.buffer);
    }

    try {
      const res = await store.dispatch("listing/uploadImages", formData);

      return res.data.map(
        (image: {
          key: string;
          Location: string;
          format: string;
          updatedAt: Date;
        }) => ({
          url: image.Location,
          format: image.format,
          updatedAt: image.updatedAt,
        })
      );
    } catch (error) {
      return toast.error("Something went wrong, try later", {
        position: POSITION.TOP_LEFT,
      });
    }
  };

const __returned__ = { loading, agentList, listing, toast, listingId, maxDate, isUploadingImage, formFields, form, rules, v$, getUsersSalesPerson, getListing, uploadImage, changeAgent, orderImages, saveListing, cancelListing, uploadImageRequest, reactive, computed, onMounted, ref, get ListingStatus() { return ListingStatus }, get StatesEnum() { return StatesEnum }, get required() { return required }, get minValue() { return minValue }, get useVuelidate() { return useVuelidate }, DatePicker, get store() { return store }, get Cleave() { return Cleave }, get phoneNumberOption() { return phoneNumberOption }, get POSITION() { return POSITION }, get useToast() { return useToast }, get UserInterface() { return UserInterface }, get router() { return router }, get ImageFilesInterface() { return ImageFilesInterface }, get dayjs() { return dayjs }, get ListingEditStatus() { return ListingEditStatus }, DropDown, get getUserData() { return getUserData }, get ImagesInterface() { return ImagesInterface } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})